/* Dynamic Plot code copyright 2015 (c) Tuva Labs Inc. */
/**
 * Average plot elements:
 *  Mean, Median, Mode (which are point averages),
 *  and Standard Deviation, Variance, and Interquartile Range (which are measures of spread)
 *
 *  Includes sample and population variants (which in some cases have identical values).
 *
 *  Each class like Dyn.MeanElement() controls all mean lines for the plot (e.g. multiple axes, multiple atributes on each axis).
 *
 *  And each type (MeanElement, StdDeviationElement) have Dyn.averageElementBase() as the parent class, which has Dyn.PlotElement
 *  as the base class.
 *
 * Added to Dyn.PlotShaper's list of plot elements, to make a plot.
 * created by jpatel, extended by cmiller.
 */

// import {Dyn} from '../../dynDataSet/dynMiscellany';
import { PlotElement} from './plotElement'
import ToolbarMenuItems from "../../../authoring/components/ToolbarMenuItems";

/**
 * Dyn.averageElementBase()
 * Base class for averages: MeanElement, MedianElement, ModeElement.
 */
class AverageElementBase extends PlotElement {

    /**
     * @constructor Construct the controller for an Average or Spread element of a plot.
     * @param Dyn -
     * @param priority - Dyn.EPlotPriority type for this average or spread
     * @param controlAndReplace - Dyn.EPlotControl type for this average or spread
     */
    constructor( Dyn, priority, controlAndReplace ) {
        super( Dyn );
        this.avgModels = [];
        this.initElement( {
            priority: priority,
            replaceType: controlAndReplace,
            primaryType: controlAndReplace,
            otherControls: [
                Dyn.EPlotControl.kControlsAverage, // true for all subclasses of AverageElementBase
                Dyn.EPlotControl.kControlsAdornmentPositionOnly ] // for fast add/remove since icons/margins don't change
        }, Dyn );
    }

    // initAverageElementBase() {
    //     this.avgModels = [];
    // }

    /**
     * Is our Average or Spread statistic a region?
     * Averages like Mean, Mode return false to indicate a line, not a region.
     * Measures of spread like Std Deviation, IQR return true (see SpreadElement.isRegion())
     */
    get isRegion() {
        return false;
    }

    save() {
        // get PlotElement base model
        let plotElementModel = super.save();

        // save label positions so they can be restored
        plotElementModel.avgModels = [];
        this.avgModels.forEach( averageOrSpreadModel =>
            plotElementModel.avgModels.push( averageOrSpreadModel.save() ) );

        return plotElementModel;
    }

    restore( savedData ) {
        let Dyn = this.dyn;

        // restore base model
        super.restore( savedData );

        if( savedData.avgModels ) {
            savedData.avgModels.forEach( ( savedAvgModel, modelIndex ) => {
                // restore AverageOrSpreadModel data, creating new model if needed
                // (e.g. 3 mean models for one numeric attribute split by 3 categories)
                if( modelIndex === this.avgModels.length ) {
                    this.avgModels.push( new Dyn.AverageOrSpreadModel() );
                }
                this.avgModels[modelIndex].restore( savedAvgModel );
            } );
        }
    }

    /**
     * Compute the averages of all attributes withing the given cell on the given axis.
     * @param cell -- information on the intersection of X or Y categories (or 1 cell for entire axis)
     * @param axisView - Dyn.AxisView
     * @param iconPositions - Dyn.PlotView.casePositions
     * @param view - parameters for paper, colors, animation, etc.
     */
    computeAveragesOfCellOnAxis( cell, axisView, iconPositions, view ) {
        let Dyn = this.dyn,
            cellValues = [],
            numAttributes = axisView.allAttributes.countActiveIncludingDualAxis();

        if( axisView.isNumberLineAxis() ) {
            // create one array per attribute on axis
            for( let i = 0; i < numAttributes; i++ ) {
                cellValues.push( [] );
            }
            // add attribute value for each icon in cell
            for( let i = 0, numCases = cell.cases.length; i < numCases; i++ ) {
                let iconIndex = cell.cases[i];
                let pos = iconPositions[iconIndex];
                let attrIndex = axisView.isHorizontal ? pos.xAttrIndex : pos.yAttrIndex;
                let attribute = axisView.allAttributes.getActiveAttribute( attrIndex );
                let attrValue = attribute.cases[pos.caseIndex];
                Dyn.assertArrayIndex( cellValues, attrIndex, "iconPosition.attrIndex more than axisView.allAttributes.countActive()" );
                if( isFinite( attrValue ) && attrIndex < cellValues.length ) {
                    cellValues[attrIndex].push( attrValue );
                }
            }
            // compute average for each attribute, if there is a valid (non-missing) result, plot the line(s)/region(s)
            for( let i = 0; i < numAttributes; i++ ) {
                this.computeOneAverageAndLine( cellValues[i], cell, axisView, i, view );
            }
        }
    }

    /**
     * Compute the averages of all attributes withing the given plot on the given axis.
     * @param populationCaseIndices
     * @param axisView - Dyn.AxisView
     * @param iconPositions - Dyn.PlotView.casePositions
     * @param view - parameters for paper, colors, animation, etc.
     */
    computeAveragesOfPopulationOnAxis( populationCaseIndices, axisView, iconPositions, view ) {
        let Dyn = this.dyn;
        if( axisView.isNumberLineAxis() ) {
            // create one array per attribute on axis
            for( let i = 0, j = axisView.allAttributes.countActiveIncludingDualAxis(); i < j; i++ ) {
                let attribute = axisView.allAttributes.getActiveAttribute( i );
                let caseValueArray = Dyn.AveragesOfSamples.getPopulationNumericCaseValuesForAttribute( attribute, populationCaseIndices );
                this.computeOneAverageAndLine( caseValueArray, /*cell,*/ axisView, i, view );
            }
        }
    }

    /**
     * Add a single line or a multi-line region on the axis, at axisValue, to show the average or St.Dev/IQR/Variance statistic.
     * Should be called multiple times to add one line/region per cell (e.g. multiple categories on cross-axis), per attribute
     * (1+ attributes on each axis), or per standard deviation (for 1SD - 3SD lines option).
     * @param avgData -- AverageOrSpreadModel with computed value on numeric axis, plus additional parameters.
     * @param cell -- rectangular boundary of the cell to draw in (plot may have 1 cell for entire axis, or one cell per category)
     * @param axisView - Dyn.AxisView X or Y axis
     * @param attrIndex - index of attribute on axis
     * @param view - parameters for paper, etc.
     */
    addLineAdornment( avgData, cell, axisView, attrIndex, view ) {
        var Dyn = this.dyn,
            plotElement = this,
            isXAxis = axisView.isHorizontal,
            attribute = axisView.getActiveAttribute( attrIndex ),
            axisView1or2 = axisView.getSubviewForActiveAttribute( attrIndex ),
            isShowingLabels = this.plotShaper.showLabelsOnStats,
            pathString;

        // function targetIsTextLabel( e ) {
        //     return (typeof e.target.textContent === "string") && (e.target.textContent.length > 0);
        // }

        function hoverColorChange( lineSet, color ) {
            let connectorLine = lineSet.getLabelConnectorEl(),
                textRect = lineSet.getLabelRectEl(),
                lineElement = lineSet.getLineElement(),
                extraLineElement = lineSet.getExtraLineElement();
            connectorLine.attr( { stroke: color } );
            textRect.attr( { fill: color } );
            lineElement.attr( { stroke: color } );
            if( extraLineElement )
                extraLineElement.attr( { stroke: color } );
        }

        function hoverIn( e ) {
            Dyn.assert( lineSet ); // from closure
            let // this === wrapperCover, extraLine, or textRect
                lineElement = lineSet.getLineElement(),
                wrapperCover = lineSet.getWrapperCover(),
                textRect = lineSet.getLabelRectEl(),
                tipOptions = {}; // x,y at mouse position
            if( e.target.raphaelid === textRect.id ) {
                // show cursor and glow if hover over label
                textRect.attr( "cursor", Dyn.plotUtil.cursorMove );
                plotElement.addGlow( lineElement, view.hoverColor, Dyn.plotUtil.lineGlowWidth );
            } else if( Dyn.toolTip.isMyElement( wrapperCover ) ) {
                // over wrapper or extra line, and already showing tooltip: update tooltip position if over line
                Dyn.toolTip.update( wrapperCover, e, tipOptions );
            } else {
                // over wrapper or extra line, not yet showing tooltip: show tooltip and glow if over line
                Dyn.toolTip.show( wrapperCover, e, tipOptions );
                plotElement.addGlow( lineElement, view.hoverColor, Dyn.plotUtil.lineGlowWidth );
            }
            hoverColorChange( lineSet, view.hoverColor ); // darken colors for hover
            // prevent flicker (#1089), otherwise addGlow() seems to cause hoverOut() then hoverIn() repeatedly
            // in Chrome. This may be because we addGlow() to lineSet (lines) vs hoverIn() to wrappercover,
            // because in other situations this flicker does not happen.
            wrapperCover.toFront(); // prevent flicker
        }

        function mouseMove( e ) {
            // update tooltip position as mouse moves over the line
            let wrapperCover = this,
                tipOptions = { x: wrapperCover.attrs.x, y: wrapperCover.attrs.y };
            if( Dyn.toolTip.isMyElement( wrapperCover ) ) {
                Dyn.toolTip.update( wrapperCover, e, tipOptions );
            }
        }

        function hoverOut( e ) {
            Dyn.assert( lineSet ); // from closure
            // this === wrapperCover, extraLine, or textRect
            let wrapperCover = lineSet.getWrapperCover(),
                textRect = lineSet.getLabelRectEl(),
                focusOnLabel = Dyn.plotUtil.isKeyboardFocus( textRect );
            e.currentTarget.style.cursor = Dyn.plotUtil.cursorDefault;
            if( Dyn.toolTip.isMyElement( wrapperCover ) ) {
                Dyn.toolTip.hide();
            }
            hoverColorChange( lineSet, view.lineColor ); // restore non-hover color
            plotElement.removeGlow();
            plotElement.plotShaper.layerAdornments(); // restore original layering
            if( focusOnLabel ) {
                Dyn.plotUtil.setAsKeyboardFocus( textRect ); // restore focus removed by layerAdornments()
            }
        }

        function clickSet( e ) {
            plotElement.selectAdornment && plotElement.selectAdornment( this.data( "adornIndex" ) );
        }

        function makeLineSetFunctions( lineSet ) {
            lineSet.getLineElement = function() {
                return this[0];
            };
            lineSet.getWrapperCover = function() {
                return this[1];
            };
            lineSet.getLabelConnectorEl = function() {
                return this[2];
            };
            lineSet.getLabelRectEl = function() {
                return this[3];
            };
            lineSet.getLabelTextEl = function() {
                return this[4];
            };
            lineSet.getRegionRectElement = function() {
                return (this[0].data( "isRegion" ) ? this[5] : null);
            };
            lineSet.getExtraLineElement = function() {
                return this[6];
            };
            lineSet.getNonOverlappingWidgetRect = function() {
                let lineSet = this,
                    textElement = this.getLabelTextEl(),
                    widgetRect = textElement.data( "widgetRect" ),
                    isOnYAxis = !lineSet.data( "isOnXAxis" ),
                    isNotRegion = (widgetRect && widgetRect.height < 1),
                    lineBuffer = 2;
                // return non-null rect to avoid overlap with only if a line on Y axis (not region, not on X axis)
                return ((isNotRegion && isOnYAxis) ?
                    (new Dyn.mathUtil.Rect( widgetRect.x, widgetRect.y - lineBuffer, widgetRect.width, lineBuffer )) :
                    null);
            };
        }

        // make single or multi-line path string
        //  - absolute-value stats like mean (e.g.=5 pounds) become a single line at one axis value/postion
        //  - relative-value stats are st.dev (e.g. 5 +- 2 pounds) passed as an array to display as a region between
        //      absolute values on axis
        let avgRect = this._makeRectOfAveragLineOrSpread( axisView1or2, cell, avgData.lines );
        if( this.isRegion ) {
            Dyn.assert( avgData.lines instanceof Array );
            pathString = "";
            avgData.lines.forEach( ( value ) => {
                pathString += this._makeLinePathString( axisView1or2, avgRect, value );
            } );
        } else {
            Dyn.assert( isFinite( avgData.axisValue ) );
            pathString = this._makeLinePathString( axisView1or2, avgRect, avgData.axisValue );
        }
        let extraLinePath = Number.isFinite( avgData.extraLineAt ) && // extra mean line/cross line for some spread elements
            this._makeExtraLinePathString( axisView1or2, avgRect, avgData.extraLineAt );
        avgData.setToolTipAttributeName( attribute, this.isPlottingDistribution() && view.isPopulationStat );
        let tipStrings = this.getToolTipString( avgData.tipData, attribute );
        Dyn.assert( tipStrings.tip && tipStrings.label );

        // create or update the line element
        const strokeWidth = view.strokeWidth || 1,
            numElementsPerLine = 1, // one set per line
            hasEnoughExistingAdornments = (view.numAdornmentsUpdated <= (view.adornments.length - numElementsPerLine));
        let lineSet = view.adornments[view.numAdornmentsUpdated],        // set that holds elements for line
            lineElement = lineSet ? lineSet.getLineElement() : null,            // visible line(s)
            wrapperCover = lineSet ? lineSet.getWrapperCover() : null,           // invisible region slightly larger than line(s) for hover
            regionRectElement = lineSet ? lineSet.getRegionRectElement() : null,    // for regions like StDev, also show as a partially transparent rectangle
            textRect = lineSet ? lineSet.getLabelRectEl() : null,    // rect for label
            connectorLine = lineSet ? lineSet.getLabelConnectorEl() : null,          // connector line for label
            textElement = lineSet ? lineSet.getLabelTextEl() : null,    // text of label
            extraLineElement = lineSet ? lineSet.getExtraLineElement() : null;
        if( view.updateExistingAdornments && hasEnoughExistingAdornments ) {
            // update adornments for this plot element
            // when updating, the animation moves the path to its new location
            lineElement.animate( {
                path: pathString,
                stroke: view.lineColor,
                'stroke-width': strokeWidth,
                opacity: 1,
                'clip-rect': view.clipRectString
            }, view.animationDelay, view.animationEasing );
            if( extraLineElement ) {
                Dyn.assert( extraLinePath );
                extraLineElement.animate( {
                    path: extraLinePath,
                    stroke: view.lineColor,
                    'stroke-width': strokeWidth,
                    opacity: 1,
                    'clip-rect': view.clipRectString
                }, view.animationDelay, view.animationEasing );
            }
            wrapperCover.attr( {
                path: pathString, stroke: Dyn.plotUtil.transparentColor, 'stroke-width': Dyn.plotUtil.lineCoverWidth,
                opacity: 1, 'clip-rect': view.clipRectString
            } );
            if( this.isRegion ) {
                this._makeRegionRectElement( regionRectElement, view, avgRect );
            }
            if( isShowingLabels ) {
                this._makeLabelElements( [ textRect, textElement, connectorLine ],
                    view, avgRect, tipStrings, isXAxis, cell, lineSet, avgData );
            }
            view.numAdornmentsUpdated++;
        } else {
            // create new adornments and add to adornments list for this plot element.
            // when creating, the animation is to the initially-invisible line/region to full opacity (so line appears gradually in updated location)
            lineSet = view.paper.set();
            lineElement = view.paper.path( pathString )
                .attr( {
                    stroke: view.lineColor,
                    'stroke-width': strokeWidth,
                    opacity: 0,
                    'clip-rect': view.clipRectString
                } )
                .animate( { opacity: 1 }, view.animationDelay, view.animationEasing )
                .data( "isRegion", this.isRegion );
            wrapperCover = lineElement.clone()
                .attr( {
                    "stroke-width": Dyn.plotUtil.lineCoverWidth,
                    stroke: Dyn.plotUtil.transparentColor,
                    'clip-rect': view.clipRectString
                } )
                .click( clickSet );
            lineSet.push( lineElement, wrapperCover ); // lineSet[0, 1]
            [ textRect, textElement, connectorLine ] = this._makeLabelElements( [],
                view, avgRect, tipStrings, isXAxis, cell, lineSet, avgData );
            textRect.hover( hoverIn, hoverOut );
            lineSet.push( connectorLine, textRect, textElement ); // lineSet[2, 3, 4]
            if( this.isRegion ) {
                regionRectElement = this._makeRegionRectElement( null, view, avgRect );
                lineSet.push( regionRectElement ); // lineSet[5]
            }
            if( extraLinePath ) {
                extraLineElement = this._makeExtraLineElement( view, extraLinePath, strokeWidth );
                extraLineElement.hover( hoverIn, hoverOut );
                lineSet.push( extraLineElement ); // lineSet[6]
            }
            makeLineSetFunctions( lineSet );
            lineSet.data( "adornIndex", view.adornments.length );
            lineSet.data( "isOnXAxis", axisView.isHorizontal );
            view.adornments.push( lineSet );
            view.numAdornmentsUpdated++;
        }
        Dyn.assert( lineSet.type === "set" && lineElement.type === "path" && wrapperCover.type === "path" );

        // update tooltip
        wrapperCover.data( "tooltip", tipStrings.tip );
        wrapperCover.hover( hoverIn, hoverOut );
        wrapperCover.mousemove( mouseMove );
        if( this.setDoubleClickHandler ) {
            this.setDoubleClickHandler( wrapperCover );
        }

        // show or hide label and connector line
        Dyn.plotUtil.showOrHideElement( textRect, isShowingLabels );
        Dyn.plotUtil.showOrHideElement( textElement, isShowingLabels );
        Dyn.plotUtil.showOrHideElement( connectorLine, isShowingLabels );

        // Store measure for use by Summary Table
        //Dyn.assertFiniteNumeric( avgData.measureValue );
        if( view.measures && view.wantSummaryMeasure && Number.isFinite( avgData.measureValue ) ) {
            let precision = this.getPrecision( attribute ),
                valueStr = this.isRegion ?
                    attribute.axisValueToStringRelativeUnits( avgData.measureValue, precision ) :
                    attribute.axisValueToString( avgData.measureValue, precision );
            this.plotShaper.measures.addToMeasuresArray( view.measures, {
                measureName: view.nameOfAverage,
                value: valueStr,
                attributeID: attribute.id,
            }, cell.xBinIndex, cell.yBinIndex );
            this._addPlotSummary( avgData, view.measures.at(-1) );
        }
        // Also store measure with Sample Statistics table if sampling
        if( this.isInSamplingMode() && view.wantSampleMeasure ) {
            // For stats summary to calculate measures for all samples
            this.plotShaper.sampleStats.calcSampleStats( attribute.id,
                view.nameOfSampleAttr || view.nameOfAverage, // use nameOfSampleAttr if available
                avgData.measureValue, this.primaryType, view.nameOfDistrAttr );
        }
    }

    /** make a path string for line at axisValue on X or Y axis, with height/width of avgRect */
    _makeLinePathString( axisView, avgRect, axisValue ) {
        var sx, sy, ex, ey;
        if( axisView.isHorizontal ) {
            sx = axisView.dataToPixel( axisValue ).toFixed( 1 );
            sy = avgRect.y;
            ex = sx;
            ey = avgRect.height + sy;
        } else { // Y or Y2 axis, see axisView.id
            sx = avgRect.x;
            sy = axisView.dataToPixel( axisValue ).toFixed( 1 );
            ex = avgRect.width + sx;
            ey = sy;
        }
        return "M" + sx + "," + sy + "L" + ex + "," + ey;
    }

    _makeRectOfAveragLineOrSpread( axisView, cell, orderedAxisValues ) {
        // get rect on X or Y axis, between axisValue1 and axisValue2, with height/width of cell
        // if a single value, the rect reduces to a horizontal or vertical line
        var Dyn = this.dyn,
            rect = new Dyn.mathUtil.Rect( cell ),
            axisValue1 = orderedAxisValues[0],
            axisValue2 = orderedAxisValues[orderedAxisValues.length - 1];
        if( axisView.isHorizontal ) {
            rect.x = axisView.dataToPixel( axisValue1 );
            rect.width = axisView.dataToPixel( axisValue2 ) - rect.x;
        } else { // Y or Y2 axis, see axisView.id
            rect.y = axisView.dataToPixel( axisValue1 );
            rect.height = axisView.dataToPixel( axisValue2 ) - rect.y;
        }
        return rect.normalize();
    }

    /** make or update the shaded region for measures of spread (St.Dev. IQR, etc). */
    _makeRegionRectElement( oldElement, view, avgRect ) {
        let Dyn = this.dyn,
            regionRectElement = oldElement;
        if( regionRectElement ) { // update the old element
            regionRectElement.animate( {
                x: avgRect.x,
                y: avgRect.y,
                width: avgRect.width,
                height: avgRect.height,
                'fill-opacity': Dyn.plotUtil.opacityOfStDevVarianceIqrRect,
                'clip-rect': view.clipRectString
            }, view.animationDelay, view.animationEasing );
        } else {
            regionRectElement = view.paper.rect( avgRect.x, avgRect.y, avgRect.width, avgRect.height )
                .attr( {
                    fill: view.lineColor, 'fill-opacity': 0,
                    'stroke-opacity': 0,
                    'clip-rect': view.clipRectString
                } )
                .animate( {
                    'fill-opacity': Dyn.plotUtil.opacityOfStDevVarianceIqrRect
                }, view.animationDelay, view.animationEasing )
            Dyn.plotUtil.disableMouseOverEvents( regionRectElement ); // so we can hover or click on overlapping adornments like mean line.
        }
        regionRectElement.data( "regionRect", avgRect ); // save for position3DotButton()
        return regionRectElement;
    }

    /** make or update the text element for "Show Label". */
    _makeLabelElements( oldElements, view, avgRect, tipStrings, isXAxis, cell, lineSet, avgModel ) {
        let Dyn = this.dyn,
            plotElement = this;

        function labelDragMove( dx, dy, x, y, event ) {
            // Note: dx and dy are positions relative to starting position (not last dragMove)
            if( plotElement.ignoreDragEvents() )
                return;
            Dyn.assert( lineSet ); // from closure
            let lineElement = lineSet.getLineElement(),
                connectorLine = lineSet.getLabelConnectorEl(),
                textRect = lineSet.getLabelRectEl(),
                textElement = lineSet.getLabelTextEl(),
                isXAxis = textElement.data( "isXAxis" ),
                widgetRect = textElement.data( "widgetRect" ),
                avgModelIndex = textElement.data( "modelIndex" );
            Dyn.assert( this === textRect );

            // update the text element
            textElement.attr( { x: textElement.data( "startX" ) + dx, y: textElement.data( "startY" ) + dy } );

            // update the connector line and text background elements
            let labelRect = Dyn.PlotLabelsUtil.getLabelRectFromTextElement( textElement ),
                connectorLineRect = Dyn.PlotLabelsUtil.getLineFromLabelToWidget( labelRect, widgetRect, isXAxis );
            Dyn.PlotLabelsUtil.saveLabelPositionAndOffset( textElement, labelRect, widgetRect );
            textRect.attr( { x: labelRect.x, y: labelRect.y, width: labelRect.width, height: labelRect.height } );
            connectorLine.attr( { path: Dyn.PlotLabelsUtil.makeConnectorLinePathString( connectorLineRect ) } );
            plotElement.addGlow( lineElement, view.lineColor, Dyn.plotUtil.lineGlowWidth ); // update glow position
            plotElement.selectAdornment && plotElement.selectAdornment( this.data( "adornIndex" ) );
            Dyn.assertArrayIndex( plotElement.avgModels, avgModelIndex );
            plotElement.avgModels[avgModelIndex].labelPositionToModel( textElement );
        }

        function labelDragStart( x, y, event ) {
            if( plotElement.ignoreDragEvents() )
                return;
            Dyn.assert( lineSet ); // from closure
            let textElement = lineSet.getLabelTextEl();
            //textElement.data("drag",true);
            textElement.data( "startX", textElement.attr( "x" ) );
            textElement.data( "startY", textElement.attr( "y" ) );
            textElement.attr( "cursor", Dyn.plotUtil.cursorMove );
        }

        function labelDragEnd( event ) {
            if( plotElement.ignoreDragEvents() )
                return;
            Dyn.assert( lineSet ); // from closure
            let textRect = lineSet.getLabelRectEl();
            Dyn.plotUtil.setAsKeyboardFocus( textRect );
            //this.data( "drag", false );
        }

        function addKeyHandlers( elementToFocus, dragStart, dragMove, dragEnd, ariaLabel ) {
            function keyDownHandler( e ) {
                switch( e.key ) { // const {key} = event; ES6+
                    case "ArrowLeft":
                    case "ArrowRight":
                    case "ArrowUp":
                    case "ArrowDown": {   // move this element one pixel in arrow's direction
                        let dist = Dyn.plotUtil.arrowKeyAcceleratedDistance( e.key ),
                            x, y /* placeholders -- x,y not used by drag handlers */,
                            element = this;
                        dragStart.call( element, x, y, e );
                        dragMove.call( element, dist.dx, dist.dy, x, y, e );
                        dragEnd.call( element, e );
                    }
                    break;
                }
            }
            Dyn.plotUtil.setKeyboardFocusable( elementToFocus, keyDownHandler.bind(elementToFocus), ariaLabel);
        }

        let textRect = oldElements[0],
            textElement = oldElements[1],
            connectorLine = oldElements[2];

        if( textElement ) {
            avgModel.modelToLabelPosition( textElement, cell );
            textElement.attr( { text: tipStrings.label, "font-size": Dyn.preferences.getFontSize() } ); // so setLabelPosition() has the current text size
            let labelRect = Dyn.PlotLabelsUtil.setLabelPosition( this, textElement, isXAxis, cell, avgRect ),
                connectorLineRect = Dyn.PlotLabelsUtil.getLineFromLabelToWidget( labelRect, avgRect, isXAxis );
            textRect.animate(
                { x: labelRect.x, y: labelRect.y, width: labelRect.width, height: labelRect.height },
                view.animationDelay, view.animationEasing );
            textElement.animate(
                { x: labelRect.centerX(), y: labelRect.centerY() },
                view.animationDelay, view.animationEasing );
            connectorLine.animate(
                { path: Dyn.PlotLabelsUtil.makeConnectorLinePathString( connectorLineRect ) },
                view.animationDelay, view.animationEasing );
        } else {
            // create the label and connector line even if hidden, so we can reveal later
            textRect = view.paper.rect( 0, 0, 0, 0, Dyn.plotUtil.statLabelRoundRectRadius )
                // note: caller sets textRect.hover()
                .drag( labelDragMove, labelDragStart, labelDragEnd )
                .attr( { opacity: 0, "fill": view.lineColor, stroke: "none" } )
                .animate( { opacity: Dyn.plotUtil.statLabelOpacity }, view.animationDelay, view.animationEasing );
            textElement = view.paper.text( 0, 0, tipStrings.label )
                .attr( {
                    opacity: 0,
                    "fill": view.textColor,
                    "text-anchor": "middle",
                    "font-size": Dyn.preferences.getFontSize(),
                    "font-family": Dyn.plotUtil.statLabelFontFamily,
                    cursor: Dyn.plotUtil.cursorMove
                } )
                .animate( { opacity: 1 }, view.animationDelay, view.animationEasing );
            Dyn.plotUtil.disableMouseOverEvents( textElement );
            connectorLine = view.paper.path( "M0,0" )
                .attr( {
                    stroke: view.lineColor,
                    'stroke-width': Dyn.plotView.statLabelConnectorLineWidth,
                    opacity: 0,
                    'clip-rect': view.clipRectString
                } )
                .animate( { opacity: 1 }, view.animationDelay, view.animationEasing );
            Dyn.plotUtil.disableMouseOverEvents( connectorLine );
            this.plotShaper.setAdornmentLayer( 1, textRect, textElement, connectorLine );
            addKeyHandlers( textRect, labelDragStart, labelDragMove, labelDragEnd,
                Dyn.PlotLabelsUtil.getAriaLabelForStatLabel( view.nameOfAverage ) );
            {   // 2nd pass is to set the label positions for the first time
                avgModel.modelToLabelPosition( textElement, cell );
                let labelRect = Dyn.PlotLabelsUtil.setLabelPosition( this, textElement, isXAxis, cell, avgRect ),
                    connectorLineRect = Dyn.PlotLabelsUtil.getLineFromLabelToWidget( labelRect, avgRect, isXAxis );
                textElement.attr( { x: labelRect.centerX(), y: labelRect.centerY() } );
                textRect.attr( { x: labelRect.x, y: labelRect.y, width: labelRect.width, height: labelRect.height } );
                connectorLine.attr( { path: Dyn.PlotLabelsUtil.makeConnectorLinePathString( connectorLineRect ) } );
            }
        }
        textElement.data( "modelIndex", view.numModelsUpdated - 1 ); // -1 because model already updated
        avgModel.labelPositionToModel( textElement );
        return [ textRect, textElement, connectorLine ];
    }

    computeOneAverageAndLine( /* cellValues, cell, axisView, attrIndex, view */ ) {
        let Dyn = this.dyn;
        Dyn.assert( false, "derived classes should override this function" );
    }

    /**
     * Get the strings to use for the tooltip when hovering over average line, and in the stat label.
     * Can be overriden for custom string.
     * @param tipData {*}
     * @param attribute {Dyn.Attribute}
     * @returns { tip:string, label:string}
     */
    getToolTipString( tipData, attribute ) {
        let Dyn = this.dyn,
            precision = this.getPrecision( attribute );

        tipData.value = attribute.axisValueToString( tipData.axisValue, precision );
        let tip = Dyn.loc( "{{nameOfAverage}} of {{attributeName}} = {{value}}", tipData );
        return {
            tip: tip,
            label: tipData.multiAttrTips ? tip :
                Dyn.loc( "{{nameOfAverage}} = {{value}}", tipData )
        };
    }

    /**
     * Does this plot have different numbers of bins or attributes from the last drawn plot?
     * @param xAxisView
     * @param yAxisView
     * @returns {boolean}
     */
    hasDifferentElements( xAxisView, yAxisView ) {
        let newCount = [
                this.plotShaper.bins.xBins.length,
                this.plotShaper.bins.yBins.length,
                xAxisView.allAttributes.countActive(),
                yAxisView.allAttributes.countActiveIncludingDualAxis()
            ].toString(),
            isDifferent = (newCount !== this.binAndAttributeCount); // compare arrays as strings
        this.binAndAttributeCount = newCount; // save element count for next time
        return isDifferent;
    }


	// MOVED to AverageViewUpdater constructor
	// _makeViewUpdater( ioAdornArray, xAxisView, yAxisView, noAnimation, isPopulation ) {

    cleanUpCalcAdornments( view ) {
        this.removeExtraAdornments( view );
        this.removeExtraModels( view );
    }

    /** after updating adornments, remove any extra adornments from the previous plot update */
	removeExtraAdornments( view ) {
        let Dyn = this.dyn;
        if( view.updateExistingAdornments ) {
            while( view.adornments.length > view.numAdornmentsUpdated ) {
                let svgElement = view.adornments.pop();
                svgElement.remove(); // remove from plot
            }
        }
        Dyn.assert( view.adornments.length === view.numAdornmentsUpdated );
        // also remove extra models at the same time
        // this.removeExtraModels( view );
    }

    /** after updating models, remove any extra models from the previous plot update */
	removeExtraModels( view ) {
        let Dyn = this.dyn;
        while( this.avgModels.length > view.numModelsUpdated ) {
            this.avgModels.pop();
        }
        Dyn.assert( this.avgModels.length == view.numModelsUpdated );
    }

    /**
     * Get the cell boundaries to display the population averages.
     * Because this.plotShaper.dataSet.getPopulationCaseArray() is for all of the population cases, ignoring cell/category subsets,
     * we want to use the entire plot area to display Population Mean, Population Median, etc.
     */
	getPopulationCell() {
        let Dyn = this.dyn,
            plotCells = this.plotShaper.bins.cells,
            cell;

        if( plotCells.length === 1 ) {
            cell = plotCells[0]; // if only one cell, it is a rect that covers entire plot interior
        } else {
            cell = new Dyn.mathUtil.Rect( this.plotShaper.plotBounds ); // else copy the reference to the plot interior
            cell.xBinIndex = 0;
            cell.yBinIndex = 0;
            cell.isPopulationCell = true;
        }
        return cell;
    }

	isViewingSample() {
        return this.plotShaper.dataSet.caseSampler.isSampleSelected();
    }

	isPlottingDistribution() {
        return this.plotShaper.dataSet.caseSampler.isDistributionSelected();
    }

	isInSamplingMode() {
        return this.plotShaper.dataSet.caseSampler.hasSamples();
    }

	getSamplingModeKey() {
        return this.plotShaper.dataSet.caseSampler.getViewKey();
    }

    /**
     * If plot is showing the population in sampling mode, then the ordinary stats
     * like "Mean" show the population mean, so we want to hide the "Population Mean",
     * if any, because it is redundant with the orinary "Mean".
     * @returns {boolean} - population statistics should be hidden
     */
	wantPopulationStatsHidden() {
        return this.plotShaper.dataSet.caseSampler.isPopulationSelected();
    }

    /** get the precision to use for this average, using rules for significant figures + user overrides + average overrides */
	getPrecision( attribute ) {
        const significantFiguresDelta = 1; // +1 for averages, from "Significant Figures - Rules & in Tuva" from Kristian Hunter-Thomson
        let Dyn = this.dyn;
        return attribute.getNumericDecimals() + significantFiguresDelta + Dyn.preferences.getPrecision();
    }

    /** Do post-processing after all averages have been positioned */
	postCalcPositionUpdate( ioCasePositions, ioAdornments, subViewRef, noAnimation ) {
    }

    /**
     * Get or create the average model behind each average or spread measurement,
     * using our view updater
     * @param view - view updater
     * @returns {AverageOrSpreadModel}
     */
	_getAverageOrSpreadModel( view ) {
        let Dyn = this.dyn,
            avgModel;
        if( view.numModelsUpdated < this.avgModels.length ) {
            avgModel = this.avgModels[view.numModelsUpdated];
        } else {
            avgModel = new Dyn.AverageOrSpreadModel();
            this.avgModels.push( avgModel );
        }
        view.numModelsUpdated++;
        return avgModel;
    }

    /**
     * Get the tooltip strings used by many of the measures of spread (StDev, MeanAvDev, etc),
     * Use the same string where possible to simplify translations.
     */
	_getMeanPlusMinusSpreadToolTip( tipData, attribute ) {
        let Dyn = this.dyn;
        Dyn.assertFiniteNumeric( tipData.mean );
        Dyn.assertFiniteNumeric( tipData.spread );
        Dyn.assert( attribute );
        let precision = this.getPrecision( attribute );
        tipData.meanValue = attribute.axisValueToString( tipData.mean, precision );
        tipData.spreadValue = attribute.axisValueToStringRelativeUnits( tipData.spread, precision );
        let tip = Dyn.loc( "{{nameOfAverage}} of {{attributeName}} = {{meanValue}}±{{spreadValue}}", tipData );
        return {
            tip: tip,
            label: ( tipData.multiAttrTips ) ? tip :
                Dyn.loc( "{{nameOfAverage}} = {{meanValue}}±{{spreadValue}}", tipData )
        };
    }

    /**
     * Common subroutine for measures of spread that show a dashed mean line.
     */
	_makeDashedMeanLineElement( view, extraLinePath, strokeWidth ) {
        let Dyn = this.dyn,
            extraLineElement = view.paper.path( extraLinePath )
            .attr( {
                stroke: view.lineColor,
                'stroke-width': strokeWidth,
                opacity: 0,
                'clip-rect': view.clipRectString
            } )
            .animate( { opacity: 1 }, view.animationDelay, view.animationEasing );
        extraLineElement.node.setAttribute( 'stroke-dasharray', Dyn.plotUtil.stdDevDashArray );
        return extraLineElement;
    }

    /** Round our calculated statistic to fix variable precision errors */
    _fixPrecision( value ) {
        let Dyn = this.dyn;
        return Dyn.mathUtil.fixPrecision( value );
    }

    /** create and save plot summary for screen readers, called when creating adornment and measure */
    _addPlotSummary( avgModel, oneMeasure ) {
        let Dyn = this.dyn,
            parts = this.plotShaper.measures.oneMeasureToPlotSummary( oneMeasure ),
            measureName = parts.measureName.toLowerCase();
        if( parts.xBinName || parts.yBinName ) {
            // "The Mean of Height (inches) is 65.3, where State is California."
            let catBinName = parts.xBinName || parts.yBinName,
                catAttrName = parts.xAttrName ? parts.xAttrName : parts.yAttrName;
            avgModel.plotSummary = Dyn.stringFormat( Dyn.plotUtil.averageCategoriesPlotSummary,
                measureName, parts.attributeName, parts.value, catAttrName, catBinName ); // parameters 0,1,2,3,4
        } else {
            // "The Mean of Height (inches) is 65.3"
            avgModel.plotSummary = Dyn.stringFormat( Dyn.plotUtil.averagePlotSummary,
                measureName, parts.attributeName, parts.value ); // parameters 0,1,2
        }
    }

    /**
     * {OVERRIDE} Get the plot summary for this average element, computed by _addPlotSummary() during adornment creation.
     * @returns {string} - string, empty if no summary
     */
    getPlotSummary( options ) {
        // combine .plotSummary strings from all average models that have one
        if( options.wantStatistics ) {
            let plotSummary = this.avgModels.filter( ( m ) => m.plotSummary ).map( ( m ) => m.plotSummary );
            return plotSummary.join(' '); // note: empty array returns ""
        }
    }

} // class AverageElementBase
export default AverageElementBase;

export const wrapDynaverageElement = function(Dyn) {

    /**
     * A temporary "view" object that tracks adornments as they are created or updated.
     * Used by addLineAdornment() and others to create/update/delete adornments to match the number
     * of averages in the plot, for one average type (e.g. mean lines for MeanElement)
     * Caller should also call removeExtraAdornments( view ) at end of update.
     */
    class AverageViewUpdater {
        constructor( plotElement, ioAdornArray, xAxisView, yAxisView, noAnimation, isPopulation ) {
            this.adornments = ioAdornArray;
            this.measures = plotElement.plotShaper.measures.getMeasuresArray( plotElement.primaryType )
            this.paper = plotElement.plotShaper.adornments.paper;
            this.animationDelay = noAnimation ? 0 : Dyn.plotUtil.adornAnimationTimeMs;
            this.animationEasing = Dyn.plotUtil.adornAnimationEasing;
                // Caller should also set these parameters for addLineAdornment():
                // lineColor
                // nameOfAverage
            this.strokeWidth = isPopulation ? Dyn.plotUtil.populationAverageWidth : 1;
            this.numAdornmentsUpdated = 0; // number of adornments updated or created (for removeExtraAdornments())
            this.numModelsUpdated = 0; // number of AverageOrSpreadModels updated or created
            this.wantSummaryMeasure = true; // if true send measure to Summary Table (can be overridden)
            this.wantSampleMeasure = true; // if true send measure to Sample Statistics table (can be overridden)
            this.clipRectString = Dyn.plotView.getPlotInteriorClipRect();
            this.multiAttrTips = // if true we want tips to show attribute names, since there are multiple attributes on at least one axis
                ( (xAxisView.isNumberLineAxis && (xAxisView.allAttributes.countActiveIncludingDualAxis() > 1))
                || (yAxisView.isNumberLineAxis && (yAxisView.allAttributes.countActiveIncludingDualAxis() > 1)));

            // determine if we can update the existing adornments or not
            if( plotElement.hasDifferentElements( xAxisView, yAxisView ) ) {
                // remove old adornments (lines), then add new ones
                // animation will show old adornments fading away, new ones appearing
                Dyn.plotUtil.removeAndEmptyArrayOfElements( this.adornments );
            } else {
                // OK to update existing adornments
                // animation will show old adornments moving into their new place
                this.updateExistingAdornments = true; // if true, ok to move existing adornment to new location
            }

            // remove old measures (values for summary table)
            this.measures.length = 0;
            if( isPopulation && plotElement.plotShaper.bins.cells.length > 1 ) {
                // if this is a population parameter, and we have multiple cells (e.g. body length by gender plot)
                // then do not send population measure to Summary Table, because it will not display correctly.
                // Note: this is a temporary workaround, to avoid an incorrect Summary Table display. First recorded as part of issue #552
                this.wantSummaryMeasure = false;
                // If desired, we can work on this to resolve differences between Summary Table measures and Sample Statistics measures
                // for example, body length by gender plot, showing Sample Mean and Population Mean
                // Current Solution (A):
                //      In Summary Table: show only Sample Mean of Body-length by Gender (to match plot lines), don't show Population Mean at all,
                //      In Sample Statistics, show Sample Mean and Population Mean, but they are not split by gender so don't match the lines visible in the Plot.
                // Example Solution B: calc population stats for each category for Summary Table but keep Sample Statistics as is
                // Example Solution C: calc population stats for each category and display in both Summary Table and Sample Statistics.
            }
        }

        setColor( lineColor ) {
            this.lineColor = lineColor;
            this.textColor = Dyn.plotUtil.statLabelTextColor;
            this.hoverColor = Dyn.PlotLabelsUtil.getHoverColorFromLabelColor( lineColor );
        }
    }
    Dyn.AverageViewUpdater = AverageViewUpdater;

    /**
     * model for the average (Mean, Median, Mode) or spread (St.Dev, Variance, Interquartile Range) measurement,
     * used by Dyn.averageElementBase to construct a visible representation.
     */
    class AverageOrSpreadModel {

        axisValue;      // value of average on axis; undefined for measures of spread
        measureValue;   // value of average or spread on axis
        extraLineAt;    // value of extra line (e.g. mean line for St. Dev. measure)
        lines   = [];
        tipData = {};
        isSpread;

        save() {
            // data need to restore after undo/redo or save/restore
            let model = {};
            if( this.normalizedCoords ) {
                model.normalizedCoords = Dyn.dataUtil.clone( this.normalizedCoords );
            }
            return model;
        }

        restore( model ) {
            // restore the label position with normalized coordinates
            if( model.normalizedCoords ) {
                this.normalizedCoords = Dyn.dataUtil.clone( model.normalizedCoords );
            }
        }

        //constructor( view ) {
        //    this.tipData.nameOfAverage = view.nameOfAverage;
        //}
        setAsPointAverage( axisValue ) {
            Dyn.assertFiniteNumeric( axisValue );
            this.axisValue    = axisValue;
            this.measureValue = axisValue;
            this.lines        = [axisValue];
            this.isSpread     = false;
            return this; // for chaining
        }

        setAsSpread( measureValue, axisLinePositions ) {
            Dyn.assertFiniteNumeric( measureValue );
            Dyn.assert( axisLinePositions instanceof Array );
            this.axisValue    = undefined;
            this.measureValue = measureValue;
            this.lines        = axisLinePositions;
            this.isSpread     = true;
            Dyn.assert( this.lines.length >= 2 );
            return this; // for chaining
        }

        setExtraLine( extraLineValue ) {
            // set or clear the value of the extra line; ok if NaN
            // Note: if set, optional methods _makeExtraLinePathString() and _makeExtraLineElement() should be defined
            Dyn.assert( extraLineValue===Math.NaN || Number.isFinite( extraLineValue ));
            this.extraLineAt = extraLineValue;
            return this; // for chaining
        }

        setToolTipParameters( view, tipData ) {
            // copy parameters used by getToolTipString() and Dyn.loc()
            if( tipData ) {
                this.tipData = tipData;
            }
            this.tipData.nameOfAverage = view.nameOfAverage;
            this.tipData.multiAttrTips = view.multiAttrTips;
            if( !this.isSpread ) {
                this.tipData.axisValue = this.axisValue;
            }
            return this; // for chaining
        }

        setToolTipAttributeName( attribute, usePopulationAttributeName ) {
            // If plot is showing a distribution in sampling mode, and we have a population statistic,
            // we want to say "Population Mean of {attributeName}" but the attribute has been renamed
            // to "Sample Means of {attributeName}", so we get a mouthful.  To fix, test for this case
            // and use the population attribute name instead of distribution attribute name
            this.tipData.attributeName =
                (usePopulationAttributeName && attribute.population && attribute.population.name ) ?
                    attribute.population.name : attribute.name;
            return this; // for chaining
        }

        /** save normalized coordinates for restore, if any */
        labelPositionToModel( textElement ) {
            let labelOffset = textElement.data("offset");
            if( labelOffset ) {
                // save relative position center of user-dragged label from average or spread
                this.normalizedCoords = [ labelOffset.x.n, labelOffset.y.n ];
                //console.log( "AverageOrSpreadModel::labelPositionToModel", this.normalizedCoords );
            }
        }

        /** restore any normalized positions that were saved, else do nothing */
        modelToLabelPosition( textElement, cellRect ) {
            let labelOffset = textElement.data("offset") || {};
            if( labelOffset && this.normalizedCoords && (this.normalizedCoords.length >= 2) ) {
                // restore the relative offset from widget to user-dragged label,
                //  which is used by PlotLabelsUtil.restoreLabelPositionAndOffset()
                labelOffset.x = new Dyn.axisUtil.NACoord( this.normalizedCoords[0] );
                labelOffset.y = new Dyn.axisUtil.NACoord( this.normalizedCoords[1] );
                textElement.data("offset",labelOffset);
                textElement.data("cellRect",cellRect);
                //console.log( "AverageOrSpreadModel::modelToLabelPosition", this.normalizedCoords );
            }
        }
    } // class AverageOrSpreadModel

// Expose this class under Dyn, for spreadElement.js
Dyn.AverageOrSpreadModel = AverageOrSpreadModel;

Dyn.averageElementBase = AverageElementBase;
// helpers for override function to call base function
//Dyn.averageElementBase.prototype.saveAverageElementBase = Dyn.averageElementBase.prototype.save;
//Dyn.averageElementBase.prototype.restoreAverageElementBase = Dyn.averageElementBase.prototype.restore;
//Dyn.averageElementBase.inheritsFrom( Dyn.PlotElement );

//-----------------------------------------------------------------------------------

    /**
     * Dyn.MeanElement()
     */
    class MeanElement extends AverageElementBase {

        constructor( Dyn ) {
            super( Dyn,
                Dyn.EPlotPriority.kPriorityMean,
                Dyn.EPlotControl.kControlsMean );
        }

        /** adornments (specific to this element) draw a mean line on the plot area */
        calcAdornments( ioAdornArray, iconPositions, xAxisView, yAxisView, noAnimation ) {
            var plotCells = this.plotShaper.bins.cells,
                view = new Dyn.AverageViewUpdater( this, ioAdornArray, xAxisView, yAxisView, noAnimation );
            view.nameOfAverage = Dyn.loc(this.kStatName[this.getSamplingModeKey()]);
            view.nameOfSampleAttr = Dyn.loc( this.kStatName.viewSample );
            view.nameOfDistrAttr = Dyn.loc(this.kStatName.nameOfDistrAttr);
            view.setColor( Dyn.plotUtil.meanColor );

            // compute the mean of any numeric attributes on X and Y axis, for each cell created by the categorical axes.
            for ( let i=0, j=plotCells.length; i<j; i++ ){
                var cell = plotCells[i];
                if( cell.cases ){
                    this.computeAveragesOfCellOnAxis( cell, xAxisView, iconPositions, view );
                    this.computeAveragesOfCellOnAxis( cell, yAxisView, iconPositions, view );
                }
            }
            this.cleanUpCalcAdornments( view );
        }

        computeOneAverageAndLine( cellValues, cell, axisView, attrIndex, view ) {
            // compute the mean for the collected attribute values in the cell, and if 1+ numeric values, plot the line on the axis
            // NOTE: bug fix #1283 use roundToPrecision() to reduce rounding errors, to match mean calculated by Bar of Means.
            let mean = this._fixPrecision( Dyn.mathUtil.meanOfNumericArray( cellValues ));

            if( isFinite( mean )) {
                let avgData = this._getAverageOrSpreadModel( view )
                    .setAsPointAverage( mean )
                    .setToolTipParameters( view, null );
                this.addLineAdornment( avgData, cell, axisView, attrIndex, view );
            }
        }

        get kStatName() {
            return {
                notSampling: "Mean",
                viewSample: "Sample Mean (x̄)",
                viewPopulation: "Population Mean (μ)",
                viewDistribution: "Mean of Distribution",
                nameOfDistrAttr: "Sample Means"
            };
        }

        get kUndoAction() { return 'Mean'; }

    } // class MeanElement

    Dyn.MeanElement = MeanElement;
    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsMean, Dyn.MeanElement );
    //Dyn.MeanElement.inheritsFrom( Dyn.averageElementBase );
    //Dyn.MeanElement.prototype.kUndoAction = 'Mean';

    /**
     * Dyn.MedianElement()
     */
    class MedianElement extends AverageElementBase {

        constructor( Dyn ) {
            super( Dyn,
                Dyn.EPlotPriority.kPriorityMedian,
                Dyn.EPlotControl.kControlsMedian );
        }

        get kUndoAction() { return 'Median'; }

        get kStatName() { return {
            notSampling: "Median",
            viewSample: "Sample Median",
            viewPopulation: "Population Median",
            viewDistribution: "Median of Distribution",
            nameOfDistrAttr: "Sample Medians"
        }}

        /** update adornments (specific to this element) to show a median line on the plot area */
        calcAdornments( ioAdornArray, iconPositions, xAxisView, yAxisView, noAnimation ) {
            var plotCells = this.plotShaper.bins.cells,
                view = new Dyn.AverageViewUpdater( this, ioAdornArray, xAxisView, yAxisView, noAnimation );
            view.nameOfAverage = Dyn.loc( this.kStatName[this.getSamplingModeKey()] );
            view.nameOfSampleAttr = Dyn.loc( this.kStatName.viewSample );
            view.nameOfDistrAttr = Dyn.loc( this.kStatName.nameOfDistrAttr );
            view.setColor( Dyn.plotUtil.medianColor );

            // compute the Median of any numeric attributes on X and Y axis, for each cell created by the categorical axes.
            for( let i = 0, j = plotCells.length; i < j; i++ ) {
                var cell = plotCells[i];
                if( cell.cases ) {
                    this.computeAveragesOfCellOnAxis( cell, xAxisView, iconPositions, view );
                    this.computeAveragesOfCellOnAxis( cell, yAxisView, iconPositions, view );
                }
            }
            this.cleanUpCalcAdornments( view );
        }

        computeOneAverageAndLine( cellValues, cell, axisView, attrIndex, view ) {
            // compute the Median for the collected attribute values in the cell, and if 1+ numeric values, plot the line on the axis
            let median = this._fixPrecision( Dyn.mathUtil.medianOfNumericArray( cellValues ));

            if( isFinite( median ) ) {
                let avgData = this._getAverageOrSpreadModel( view )
                    .setAsPointAverage( median )
                    .setToolTipParameters( view, null );
                this.addLineAdornment( avgData, cell, axisView, attrIndex, view );
            }
        }
    } // class MedianElement

    Dyn.MedianElement = MedianElement;
    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsMedian, Dyn.MedianElement );

//-----------------------------------------------------------------------------------

    /**
     * Dyn.ModeElement()
     */
    class ModeElement extends AverageElementBase {

        constructor( Dyn ) {
            super( Dyn,
                Dyn.EPlotPriority.kPriorityMode,
                Dyn.EPlotControl.kControlsMode );
        }

        get kUndoAction() {
            return 'Mode';
        }

        /** Update adornments (specific to this element) to show a mode line on the plot area */
        calcAdornments( ioAdornArray, iconPositions, xAxisView, yAxisView, noAnimation ) {
            var plotCells = this.plotShaper.bins.cells,
                view = new Dyn.AverageViewUpdater( this, ioAdornArray, xAxisView, yAxisView, noAnimation );
            view.nameOfAverage = Dyn.loc( "Mode" );
            view.wantSampleMeasure = false; // Don't show Mode in Sample Statistics table
            view.setColor( Dyn.plotUtil.modeColor );

            // compute the mode of any numeric attributes on X and Y axis, for each cell created by the categorical axes.
            for( let i = 0, j = plotCells.length; i < j; i++ ) {
                var cell = plotCells[i];
                if( cell.cases ) {
                    this.computeAveragesOfCellOnAxis( cell, xAxisView, iconPositions, view );
                    this.computeAveragesOfCellOnAxis( cell, yAxisView, iconPositions, view );
                }
            }
            this.cleanUpCalcAdornments( view );
        }

        computeOneAverageAndLine( cellValues, cell, axisView, attrIndex, view ) {
            // compute the mode(s) for the collected attribute values in the cell
            // unlike mean and median which have 0 or 1 average per attribute, modes can be multi-modal, up to one line per 2 cases.
            var plotElement = this,
                modeArray = Dyn.mathUtil.modeOfNumericArray( cellValues );

            modeArray.forEach( value => {
                Dyn.assert( Dyn.mathUtil.isFinite( value ) );
                value = this._fixPrecision( value );
                let avgData = this._getAverageOrSpreadModel( view )
                    .setAsPointAverage( value )
                    .setToolTipParameters( view, null );
                plotElement.addLineAdornment( avgData, cell, axisView, attrIndex, view );
            } );
        }
    } // class ModeElement

    Dyn.ModeElement = ModeElement;
    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsMode, Dyn.ModeElement );

    //-----------------------------------------------------------------------------------

    /**
     * Dyn.PopulationMeanElement()
     */
    class PopulationMeanElement extends AverageElementBase {

        constructor( Dyn ) {
            super( Dyn,
                Dyn.EPlotPriority.kPriorityPopulationMean,
                Dyn.EPlotControl.kControlsPopulationMean );
        }

        get kUndoAction() {
            return 'Population Mean ( μ )';
        }

        /** adornments (specific to this element) draw a mean line on the plot area */
        calcAdornments( ioAdornArray, iconPositions, xAxisView, yAxisView, noAnimation ) {
            let populationArray = this.plotShaper.dataSet.getPopulationCaseArray(),
                view = new Dyn.AverageViewUpdater( this, ioAdornArray, xAxisView, yAxisView, noAnimation, (populationArray !== null) );
            view.nameOfAverage = Dyn.loc( "Population Mean (μ)" );
            view.isPopulationStat = true;
            view.setColor( Dyn.plotUtil.meanColor );

            // compute the population mean of any numeric attributes on X and Y axis
            if( populationArray.length > 0 && !this.wantPopulationStatsHidden() ) {
                this.computeAveragesOfPopulationOnAxis( populationArray, xAxisView, iconPositions, view );
                this.computeAveragesOfPopulationOnAxis( populationArray, yAxisView, iconPositions, view );
            }
            this.cleanUpCalcAdornments( view );
        }

        computeOneAverageAndLine( values, axisView, attrIndex, view ) {
            // compute the mean for the collected attribute values in the cell, and if 1+ numeric values, plot the line on the axis
            let mean = this._fixPrecision( Dyn.mathUtil.meanOfNumericArray( values ));

            if( isFinite( mean ) ) {
                let avgData = this._getAverageOrSpreadModel( view )
                        .setAsPointAverage( mean )
                        .setToolTipParameters( view, null),
                    cell = this.getPopulationCell();
                this.addLineAdornment( avgData, cell, axisView, attrIndex, view );
            }
        }

    } // class PopulationMeanElement

    Dyn.PopulationMeanElement = PopulationMeanElement;
    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsPopulationMean, Dyn.PopulationMeanElement );

//-----------------------------------------------------------------------------------

    /**
     * Dyn.PopulationMedianElement()
     */
    class PopulationMedianElement extends AverageElementBase {

        constructor( Dyn ) {
            super( Dyn,
                Dyn.EPlotPriority.kPriorityPopulationMedian,
                Dyn.EPlotControl.kControlsPopulationMedian );
        }

        get kUndoAction() {
            return 'Population Median';
        }

        /** adornments (specific to this element) draw a median line on the plot area */
        calcAdornments( ioAdornArray, iconPositions, xAxisView, yAxisView, noAnimation ) {
            var //plotCells = this.plotShaper.bins.cells,
                populationArray = this.plotShaper.dataSet.getPopulationCaseArray(),
                view = new Dyn.AverageViewUpdater( this, ioAdornArray, xAxisView, yAxisView, noAnimation, (populationArray !== null) );
            view.nameOfAverage = Dyn.loc( "Population Median" );
            view.isPopulationStat = true;
            view.setColor( Dyn.plotUtil.medianColor );

            // compute the population median of any numeric attributes on X and Y axis
            if( populationArray.length > 0 && !this.wantPopulationStatsHidden() ) {
                this.computeAveragesOfPopulationOnAxis( populationArray, xAxisView, iconPositions, view );
                this.computeAveragesOfPopulationOnAxis( populationArray, yAxisView, iconPositions, view );
            }
            this.cleanUpCalcAdornments( view );
        }

        computeOneAverageAndLine( values, axisView, attrIndex, view ) {
            // compute the median for the collected attribute values in the cell, and if 1+ numeric values, plot the line on the axis
            let median = this._fixPrecision( Dyn.mathUtil.medianOfNumericArray( values ));

            if( isFinite( median ) ) {
                let avgData = this._getAverageOrSpreadModel( view )
                        .setAsPointAverage( median )
                        .setToolTipParameters( view, null ),
                    cell = this.getPopulationCell();
                this.addLineAdornment( avgData, cell, axisView, attrIndex, view );
            }
        }

    } // class PopulationMedianElement

    Dyn.PopulationMedianElement = PopulationMedianElement;
    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsPopulationMedian, Dyn.PopulationMedianElement );

//-----------------------------------------------------------------------------------

/**
 * Sample Statistics calculator for the Random Sampling results table.
 * Sample statistics are calculated here so that we can compare and make sure they are compatible with the above plot elements.
 */
class AveragesOfSamples {
    /**
     * Get an array of numeric (non-missing) case values of the attribute, for this sample.
     * @param attribute {Dyn.Attribute} -- attribute to get case data values from.
     * @param sampleCaseIndices -- selected case indices of the POPULATION which are included in this sample.
     * @returns {Array}
     */
    static getSampleNumericCaseValuesForAttribute( attribute, sampleCaseIndices ) {
        let numericArray = [],
            caseFormat = {},
            attributeTypeIsNumerical =( attribute.getDataType() === Dyn.dataUtil.types.kNumerical ),
            attributeIsSample = attribute.isDerivedFromSampling();
        if( attributeTypeIsNumerical ) {
            sampleCaseIndices.forEach( (popCaseIndex) => {
                let caseValue = attributeIsSample ?
                        attribute.population.cases[ popCaseIndex ] :
                        attribute.originalCases[ popCaseIndex ], // pre-calculate before sampling applied: use pre-filter cases
                    caseType = Dyn.dataUtil.getDataType( caseValue, caseFormat, attribute.dataType, attribute.subType ); // forced conversions might happen here
                if( caseType==="number" && caseFormat.subtype ) {
                    // convert string to number, using value found by subtype (e.g. date serial number/extracted number in string)
                    caseValue = caseFormat.value;
                }
                if( Dyn.dataUtil.isNumber( caseValue )) { // if finite number
                    numericArray.push( caseValue );
                }
            });
        }
        //console.log( "getSampleNumericCaseValuesForAttribute", attribute.name, numericArray );
        return numericArray;
    }

    static getPopulationNumericCaseValuesForAttribute( attribute, populationCaseIndices ) {
        let caseValueArray = [];
        if( attribute.population ) {
            populationCaseIndices.forEach( (pci) => {
                Dyn.assertArrayIndex( attribute.population.cases, pci, "invalid population case index for "+attribute.id );
                let dataValue = attribute.population.cases[ pci ];
                if( Dyn.dataUtil.isNumber( dataValue )) {
                    caseValueArray.push( dataValue );
                }
            });
        }
        return caseValueArray;
    }

    /**
     * Calculate a sample statistic
     * @param controlType -- Dyn.EPlotControl value
     * @param attribute {Dyn.Attribute} -- attribute which controls data formatting
     * @param sampleValues {array} -- numeric values from getSampleNumericCaseValuesForAttribute()
     * @param plotValue {number} -- the numeric value calculated by the plot element.
     * @returns {number}
     */
    static calcAverage( controlType, attribute, sampleValues, plotValue ) {
        let averageValue = Dyn.dataUtil.kMissingValue,
            precision = this.getPrecision( attribute ),
            isRelativeUnit = false;
        switch( controlType ) {
        case Dyn.EPlotControl.kControlsMean:
            averageValue = Dyn.mathUtil.meanOfNumericArray( sampleValues );
            break;
        case Dyn.EPlotControl.kControlsMedian:
            averageValue = Dyn.mathUtil.medianOfNumericArray( sampleValues );
            break;
        case Dyn.EPlotControl.kControlsMeanAvDev:
            averageValue = Dyn.mathUtil.meanAvDevOfNumericArray( sampleValues );
            isRelativeUnit = true;
            break;
        case Dyn.EPlotControl.kControlsStDeviation:
            averageValue = Dyn.mathUtil.stDevOfNumericArray( sampleValues );
            isRelativeUnit = true;
            break;
        case Dyn.EPlotControl.kControlsVariance:
            averageValue = Dyn.mathUtil.varianceOfNumericArray( sampleValues );
            isRelativeUnit = true;
            break;
        case Dyn.EPlotControl.kControlsInterQuartRange: {
                let quartiles = Dyn.mathUtil.quartilesOfNumericArray( sampleValues );
                averageValue  = quartiles.IQR;
                isRelativeUnit = true;
            }
            break;
        case Dyn.EPlotControl.kControlsStdError:
                averageValue = Dyn.mathUtil.stdErrorOfNumericArray( sampleValues );
                isRelativeUnit = true;
                break;
        case Dyn.EPlotControl.kControlsPopulationMean:
        case Dyn.EPlotControl.kControlsPopulationMedian:
            averageValue = plotValue;
            break;
        case Dyn.EPlotControl.kControlsPopulationStDeviation:
        case Dyn.EPlotControl.kControlsPopulationVariance:
        case Dyn.EPlotControl.kControlsPopulationInterQuartRange:
            averageValue = plotValue;
            isRelativeUnit = true;
            break;
        default:
            console.log( "Dyn.AveragesOfSamples.calcAverage: unknown measure name: " + controlType );
        }
        if( Number.isFinite( averageValue )) {
            // for consistency with AverageElementBase._fixPrecision()
            averageValue = Dyn.mathUtil.fixPrecision( averageValue );
        } else {
            // Change NaN result to Null, for axisValueToString(). Happens if all cases in sample are missing.
            averageValue = Dyn.dataUtil.normalizeCaseValueString( averageValue );
        }
        let displayValue = isRelativeUnit ?
            attribute.axisValueToStringRelativeUnits( averageValue, precision ) :
            attribute.axisValueToString( averageValue, precision );
        return { real: averageValue, rounded: displayValue, precision:precision };
    }

    static /*const*/ significantFiguresDelta = 1; // +1 for averages, from "Significant Figures - Rules & in Tuva" from Kristian Hunter-Thomson

    /** get the precision to use for this average, using rules for significant figures + user overrides + average overrides */
    static getPrecision( attribute ) {
        return attribute.getNumericDecimals() + this.significantFiguresDelta + Dyn.preferences.getPrecision();
    }

    /** get the precision for use of measures of this average. */
    static getOriginalPrecision( attribute ) {
        // unlike getPrecision(), we don't include Dyn.preferences.getPrecision()
        if( attribute.isDerivedFromSampling()){
            return attribute.population.origNumericPrecision + this.significantFiguresDelta;
        } else {
            return attribute.getNumericDecimals() + this.significantFiguresDelta;
        }
    }

    static getSampleMeasureName( plotElement ) {
        return( plotElement && plotElement.kStatName && plotElement.kStatName.viewSample ) ?
            Dyn.loc( plotElement.kStatName.viewSample ): "";
    }

    static getDistributionAttributeName( plotElement ) {
        return( plotElement && plotElement.kStatName && plotElement.kStatName.nameOfDistrAttr ) ?
            Dyn.loc( plotElement.kStatName.nameOfDistrAttr ): "";
    }

    static unitTest() {
        let unitTester = new Dyn.UnitTester( "Dyn.AveragesOfSamples.unitTest()" );

        // test values and compare to https://www.mathsisfun.com/data/standard-deviation.html
        // and https://www.mathsisfun.com/data/standard-deviation-formulas.html
        const   isPopulation = true,
                isSample = false;
        let d = [ 600, 470, 170, 430, 300 ],
            e = [ 9, 2, 5, 4, 12, 7, 8, 11, 9, 3, 7, 4, 12, 5, 4, 10, 9, 6, 9, 4 ],
            f = [9, 2, 5, 4, 12, 7],
            mean = Dyn.mathUtil.meanOfNumericArray( d ),
            median = Dyn.mathUtil.medianOfNumericArray( d ),
            stDevPop = Dyn.mathUtil.stDevOfNumericArray( d, isPopulation ),
            stDevSample = Dyn.mathUtil.stDevOfNumericArray( d, isSample ),
            variancePop = Dyn.mathUtil.varianceOfNumericArray( d, isPopulation ),
            varianceSample = Dyn.mathUtil.varianceOfNumericArray( d, isSample ),
            q = Dyn.mathUtil.quartilesOfNumericArray( d, true );

        function assertEqual( a, b ) {
            unitTester.test( a === b, "Dyn.AveragesOfSamples.unitTest() failure, "+a+" !== "+b  );
        }

        assertEqual( mean, 394 );
        assertEqual( median, 430 );
        assertEqual( variancePop,  21704 );
        assertEqual( stDevPop,  Math.sqrt( 21704 ));
        assertEqual( varianceSample,  27130 );
        assertEqual( stDevSample,  Math.sqrt( 27130 ));

        assertEqual( q.q1, Dyn.mathUtil.useExcludeMedianMethodForQuartiles ? 235 : 300 );
        assertEqual( q.q3, Dyn.mathUtil.useExcludeMedianMethodForQuartiles ? 535 : 470 );
        assertEqual( q.median, 430 );
        assertEqual( q.IQR, Dyn.mathUtil.useExcludeMedianMethodForQuartiles ? 300 : 170 );
        assertEqual( q.whisker1, 170 );
        assertEqual( q.whisker2, 600 );

        assertEqual( 7,  Dyn.mathUtil.meanOfNumericArray( e ));
        assertEqual( 8.9,  Dyn.mathUtil.varianceOfNumericArray( e, isPopulation ));
        assertEqual( 2.983,  Dyn.mathUtil.roundToDecimalPlaces( Dyn.mathUtil.stDevOfNumericArray( e, isPopulation ), 3 ));

        assertEqual( 6.5,  Dyn.mathUtil.meanOfNumericArray( f ));
        assertEqual( 13.1,  Dyn.mathUtil.varianceOfNumericArray( f, isSample ));
        assertEqual( 3.619,  Dyn.mathUtil.roundToDecimalPlaces( Dyn.mathUtil.stDevOfNumericArray( f, isSample ), 3 ));

        unitTester.report();
        return true;
    }
}
Dyn.AveragesOfSamples = AveragesOfSamples;

// enabled for debugging only
Dyn.debugAveragesOfSamples_UnitTestHasRun = ( Dyn.debugging ? Dyn.AveragesOfSamples.unitTest() : false );


} // wrapDynaverageElement
